import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import PunchStatusCards from "../components/Attendance/PunchStatusCards";
import UserActivity from "../components/Dashboard/UserActivity";
import Table from "../components/UI/Table";
import {
  CloseIcon,
  ViewIcon,
  SearchIcon,
  ChevronDownIcon,
} from "../Icons";
import { request } from "../utils/api";
import useCurrentUser from "../hooks/useCurrentUser";
import {
  PAGE_SHELL,
  PAGE_HEADER_ROW,
  PAGE_SUBTITLE,
  CELL,
  CELL_MUTED,
  CELL_STRONG,
  MODAL_BACKDROP,
} from "../ui/pageChrome";
import {
  PAYROLL_THEME,
  PAYROLL_BTN,
  PAYROLL_COL,
  PAYROLL_FIELD,
  PAYROLL_ROLE_CHIP,
} from "./payrollTheme";
import { isEmployeeInactiveStatus } from "../constants/employeeStatus";

const ATTENDANCE_FILTERS = [
  { value: "", label: "All Attendance" },
  { value: "clocked_in", label: "Clocked In" },
  { value: "clocked_out", label: "Clocked Out" },
  { value: "on_break", label: "On Break" },
  { value: "not_clocked_in", label: "Not Clocked In" },
];

const STATUS_FILTERS = [
  { value: "", label: "All Status" },
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const attendanceKey = (attendance) => {
  if (!attendance) return "not_clocked_in";
  if (attendance.isOnBreak || attendance.currentStatus === "on_break") {
    return "on_break";
  }
  return attendance.currentStatus || "not_clocked_in";
};

const Payroll = () => {
  const { isAdmin } = useCurrentUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });
  const [statistics, setStatistics] = useState(null);
  const [sortKey, setSortKey] = useState("firstName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [attendanceFilter, setAttendanceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchOfficeAssistants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await request(
        `/attendance/office-assistants?page=${pagination.page}&limit=${pagination.limit}&status=Active`,
      );
      if (response.status === 1) {
        const activeUsers = (response.result.users || []).filter(
          (u) => !isEmployeeInactiveStatus(u.status),
        );
        setUsers(activeUsers);
        setStatistics(response.result.statistics || null);
        setPagination((prev) => ({
          ...prev,
          total: response.result.total,
          totalPages: response.result.totalPages,
        }));
      } else {
        toast.error(response.message || "Failed to fetch office assistants");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    fetchOfficeAssistants();
  }, [fetchOfficeAssistants]);

  const hasActiveFilters = Boolean(
    search.trim() || attendanceFilter || statusFilter,
  );

  const clearFilters = () => {
    setSearch("");
    setAttendanceFilter("");
    setStatusFilter("");
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (users || []).filter((user) => {
      if (statusFilter && user.status !== statusFilter) return false;
      if (attendanceFilter) {
        const key = attendanceKey(user.currentDayAttendance);
        if (key !== attendanceFilter) return false;
      }
      if (!q) return true;
      const name = `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();
      const email = (user.email || "").toLowerCase();
      const role = (user.role?.name || "").toLowerCase();
      return name.includes(q) || email.includes(q) || role.includes(q);
    });
  }, [users, search, attendanceFilter, statusFilter]);

  const sortedUsers = useMemo(() => {
    if (!filteredUsers.length) return [];
    const sorted = [...filteredUsers].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];

      if (sortKey.includes(".")) {
        const keys = sortKey.split(".");
        aVal = keys.reduce((obj, key) => (obj || {})[key], a);
        bVal = keys.reduce((obj, key) => (obj || {})[key], b);
      }

      if (sortKey === "currentDayAttendance") {
        aVal = attendanceKey(a.currentDayAttendance);
        bVal = attendanceKey(b.currentDayAttendance);
      }

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === "string") {
        return sortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [filteredUsers, sortKey, sortOrder]);

  const handleSort = (field) => {
    if (sortKey === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortKey(field);
      setSortOrder("asc");
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleViewTimeCard = (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  const renderAttendanceStatus = (attendance) => {
    if (!attendance) {
      return <span className={`${CELL_MUTED} italic`}>N/A</span>;
    }

    const { currentStatus, isOnBreak } = attendance;

    if (isOnBreak || currentStatus === "on_break") {
      return (
        <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md border text-[10px] font-semibold bg-amber-50 text-amber-700 border-amber-200">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
          On Break
        </span>
      );
    }

    switch (currentStatus) {
      case "clocked_in":
        return (
          <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md border text-[10px] font-semibold bg-emerald-50 text-emerald-700 border-emerald-200">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Clocked In
          </span>
        );
      case "clocked_out":
        return (
          <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md border text-[10px] font-semibold bg-orange-50 text-orange-700 border-orange-200">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
            Clocked Out
          </span>
        );
      case "not_clocked_in":
        return (
          <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md border text-[10px] font-semibold bg-slate-50 text-slate-600 border-slate-200">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
            Not Clocked In
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center h-5 px-1.5 rounded-md border text-[10px] font-semibold bg-slate-50 text-slate-600 border-slate-200">
            {currentStatus?.replace(/_/g, " ") || "Unknown"}
          </span>
        );
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Employee",
        accessor: "firstName",
        sortable: true,
        className: PAYROLL_COL.employee,
        render: (user) => (
          <span className={CELL_STRONG}>
            {[user.firstName, user.lastName].filter(Boolean).join(" ") || "—"}
          </span>
        ),
      },
      {
        header: "Email",
        accessor: "email",
        sortable: true,
        className: PAYROLL_COL.email,
        render: (user) => (
          <span className={`block truncate ${CELL_MUTED}`} title={user.email || ""}>
            {user.email || "—"}
          </span>
        ),
      },
      {
        header: "Attendance",
        accessor: "currentDayAttendance",
        sortable: true,
        className: PAYROLL_COL.attendance,
        render: (user) => renderAttendanceStatus(user.currentDayAttendance),
      },
      {
        header: "Work Mode",
        accessor: "workMode",
        sortable: true,
        className: PAYROLL_COL.workMode,
        render: (user) => (
          <span className={CELL}>{user.workMode || "WFO"}</span>
        ),
      },
      {
        header: "Role",
        accessor: "role.name",
        sortable: true,
        className: PAYROLL_COL.role,
        render: (user) => (
          <span
            className={`inline-flex items-center h-5 px-1.5 rounded-md text-[10px] font-semibold uppercase tracking-wide border ${PAYROLL_ROLE_CHIP}`}
          >
            {user.role?.name || "—"}
          </span>
        ),
      },
      {
        header: "Status",
        accessor: "status",
        sortable: true,
        className: PAYROLL_COL.status,
        render: (user) => (
          <span
            className={`inline-flex items-center h-5 px-1.5 rounded-md border text-[10px] font-semibold ${
              user.status === "Active"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-slate-50 text-slate-600 border-slate-200"
            }`}
          >
            {user.status || "—"}
          </span>
        ),
      },
      {
        header: "Actions",
        accessor: "actions",
        className: PAYROLL_COL.actions,
        render: (user) => (
          <div className="inline-flex items-center justify-end">
            <button
              type="button"
              className={PAYROLL_BTN.soft}
              onClick={() => handleViewTimeCard(user)}
              title="View time card"
              aria-label={`View time card for ${user.firstName || "employee"}`}
            >
              <ViewIcon size={13} />
              View
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className={PAGE_SHELL}>
      <div className={PAGE_HEADER_ROW}>
        <div>
          <h1 className={`text-2xl font-bold ${PAYROLL_THEME.section.title}`}>
            Payroll
          </h1>
          <p className={PAGE_SUBTITLE}>
            Office assistant attendance and time cards
          </p>
        </div>
      </div>

      <PunchStatusCards statistics={statistics} />

      <div
        className={`bg-white rounded-xl border shadow-sm overflow-hidden ${PAYROLL_THEME.section.wrap}`}
      >
        <div
          className={`flex items-center gap-2 h-11 px-4 border-b box-border ${PAYROLL_THEME.section.head}`}
        >
          <span
            className={`w-0.5 h-4 rounded-full shrink-0 ${PAYROLL_THEME.section.bar}`}
            aria-hidden
          />
          <h2
            className={`min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide truncate ${PAYROLL_THEME.section.title}`}
          >
            Office Assistants
          </h2>
          <span
            className={`inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded text-[10px] font-semibold tabular-nums shrink-0 ${PAYROLL_THEME.section.badge}`}
          >
            {sortedUsers.length}
            {hasActiveFilters ? ` / ${pagination.total}` : ""}
          </span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 px-3 py-2.5 border-b border-indigo-100">
          <div className="relative flex-1 min-w-0">
            <SearchIcon
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search by name, email, role..."
              className={PAYROLL_FIELD.search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <CloseIcon size={14} />
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <div className="relative">
              <label className="sr-only" htmlFor="payroll-attendance-filter">
                Filter by attendance
              </label>
              <select
                id="payroll-attendance-filter"
                className={`${PAYROLL_FIELD.select} min-w-[150px] ${
                  attendanceFilter ? PAYROLL_FIELD.selectActive : ""
                }`}
                value={attendanceFilter}
                onChange={(e) => setAttendanceFilter(e.target.value)}
              >
                {ATTENDANCE_FILTERS.map((option) => (
                  <option key={option.value || "all-att"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>

            <div className="relative">
              <label className="sr-only" htmlFor="payroll-status-filter">
                Filter by status
              </label>
              <select
                id="payroll-status-filter"
                className={`${PAYROLL_FIELD.select} min-w-[130px] ${
                  statusFilter ? PAYROLL_FIELD.selectActive : ""
                }`}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_FILTERS.map((option) => (
                  <option key={option.value || "all-status"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>

            {hasActiveFilters ? (
              <button
                type="button"
                onClick={clearFilters}
                className={PAYROLL_BTN.clear}
              >
                <CloseIcon size={12} />
                Clear
              </button>
            ) : null}
          </div>
        </div>

        <Table
          columns={columns}
          data={sortedUsers}
          loading={loading}
          sortKey={sortKey}
          sortOrder={sortOrder}
          onSort={handleSort}
          density="compact"
          embedded
          tableClassName="table-fixed"
          srNoClassName={PAYROLL_COL.srNo}
          headTone={PAYROLL_THEME.tableHead}
          pagination={{
            currentPage: pagination.page,
            totalPages: pagination.totalPages,
            onPageChange: (newPage) =>
              setPagination((prev) => ({ ...prev, page: newPage })),
            totalItems: pagination.total,
            itemsPerPage: pagination.limit,
          }}
          noDataMessage="No office assistants found."
        />
      </div>

      {modalOpen && selectedUser ? (
        <div
          className={MODAL_BACKDROP}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            className={`w-full max-w-[min(96vw,1280px)] h-[92vh] rounded-xl border bg-white shadow-xl flex flex-col overflow-hidden ${PAYROLL_THEME.section.wrap}`}
          >
            <div
              className={`flex items-center justify-between gap-3 px-4 py-3 border-b shrink-0 ${PAYROLL_THEME.modalHead}`}
            >
              <div className="min-w-0">
                <h3
                  className={`text-base font-semibold ${PAYROLL_THEME.modalTitle}`}
                >
                  Time card details
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[13px] text-slate-500">
                    Viewing activity for
                  </span>
                  <span
                    className={`text-[13px] font-medium px-2 py-0.5 rounded-md border ${PAYROLL_THEME.chip}`}
                  >
                    {selectedUser.firstName} {selectedUser.lastName}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className={PAYROLL_BTN.modalClose}
                title="Close"
                aria-label="Close"
              >
                <CloseIcon size={14} />
              </button>
            </div>

            <div className="px-4 py-3 overflow-y-auto flex-1 bg-slate-50/50 min-w-0">
              <UserActivity
                userId={selectedUser.id}
                employeeName={`${selectedUser.firstName} ${selectedUser.lastName}`}
                employeeStatus={selectedUser.status}
                showPrintButton={isAdmin}
                isAdminView={isAdmin}
                embedded
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Payroll;
